import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import styled from 'styled-components'

import {
  useFetchTicketProviderTicketAttachmentsQuery,
} from 'api/ticketProviderTickets'

import {
  useAnchorScroll, useHashIdByKey,
  useLocationQueryParams, useSetSearchParamsWithQueryString,
} from 'utils/hooks'
import { useExpandablePageSection } from 'components/Shared/ExpandablePageSection/hooks'

import { capitalizeFirstLetter, stringifyQuery } from 'utils/strings'

import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import ExpandablePageSection from 'components/Shared/ExpandablePageSection/Index'
import FilterPanel from 'components/Shared/Skeleton/FilterPanel/Index'
import FilterPanelTitle from 'components/Shared/Skeleton/FilterPanel/FilterPanelTitle'
import UnwrappableSection from 'components/Shared/Skeleton/FilterPanel/UnwrappableSection'
import FilterWithDropdown from 'components/Shared/FilterWithDropdown/Index'
import FilterPanelSearch from 'components/Shared/Skeleton/FilterPanel/FilterPanelSearch'
import ClearFiltersIcon from 'components/Shared/ClearFiltersIcon/Index'
import SortDropdown from 'components/Shared/SortDropdown/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import AttachmentsTable from './AttachmentsTable'
import Pagination from 'components/Shared/Pagination/Index'

const StyledFilterPanel = styled(FilterPanel)`
  padding-top: 18px;
`

const FilterAndTable = styled.div`
  position: relative;
`

const TicketAttachments = ({ ticket }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    isExpanded,
    toggleExpanded,
  } = useExpandablePageSection()

  const [locationQueryParams, location] = useLocationQueryParams()
  const setQueryParam = useSetSearchParamsWithQueryString()

  const activePage = parseInt(locationQueryParams.attachments_page) || 1
  const searchValue = locationQueryParams.attachments_search || ''
  const partnersFilter = locationQueryParams.attachments_partners_filter || []
  const extensionsFilter = locationQueryParams.attachments_extensions_filter || []
  const sortField = locationQueryParams.attachments_sort_field || 'external_created_at'
  const sortDir = locationQueryParams.attachments_sort_dir || 'desc'
  const queryHashAttachmentId = useHashIdByKey('attachments')

  const noTicket = Boolean(!ticket.id || !ticket.canShowAttachments)

  const { data, isFetching } = useFetchTicketProviderTicketAttachmentsQuery({
    ticketProviderId: ticket.ticketProviderId,
    ticketId: ticket.externalId,
    page: activePage,
    partners: partnersFilter,
    extensions: extensionsFilter,
    searchValue: searchValue,
    sortField: sortField,
    sortDir: sortDir,
    attachmentId: queryHashAttachmentId,
  },
  {
    skip: noTicket,
  })

  const attachments = data?.attachments || []
  const attachmentsCount = data?.meta?.count || 0
  const totalPages = data?.meta?.totalPages || 1
  const partnersList = data?.meta?.partnerList || []
  const extensionsList = data?.meta?.extensionList || []
  const attachmentsPage = queryHashAttachmentId && data?.meta?.page

  useEffect(() => {
    if (isFetching || !attachmentsPage) {
      return
    }

    navigate(`?${stringifyQuery({ attachments_page: attachmentsPage })}#attachments-${queryHashAttachmentId}`)
  }, [isFetching, attachmentsPage, activePage, navigate, queryHashAttachmentId])

  const sortFieldsOptions = ['external_created_at', 'type', 'size', 'filename'].map(sortField => (
    {
      value: sortField,
      label: t(`ticketProviderTicketPage.attachmentsFilter.sortFieldLabels.${sortField}`),
    }
  ))

  const partnersOptions = partnersList.map(partner => (
    {
      value: partner,
      label: capitalizeFirstLetter(partner),
    }
  ))

  const extensionsOptions = extensionsList.map(extension => (
    {
      value: extension,
      label: extension,
    }
  ))

  // if anchor is set in URL,
  // scroll to the attachments section after ticket is loaded OR
  // scroll to the specific attachment after ticket and its attachments are loaded
  const anchorElementId = useAnchorScroll({
    skip: Boolean(noTicket || isFetching),
  })

  if (noTicket) {
    return null
  }

  const getDefaultUrlParams = () => {
    const params = {
      attachments_search: searchValue,
      attachments_partners_filter: partnersFilter,
      attachments_extensions_filter: extensionsFilter,
      attachments_sort_field: sortField,
      attachments_sort_dir: sortDir,
    }

    return params
  }

  const updateURL = (params) => {
    const newParams = {
      ...locationQueryParams,
      ...getDefaultUrlParams(),
      ...params,
      attachments_page: 1,
    }

    setQueryParam(newParams)
  }

  const onSearch = (newSearchValue) => {
    updateURL({
      attachments_search: newSearchValue,
    })
  }

  const onFilterChange = (value, filterName) => {
    updateURL({
      [filterName]: value,
    })
  }

  const getClearFiltersUrl = () => {
    const queryParams = { ...locationQueryParams }
    delete queryParams['attachments_search']
    delete queryParams['attachments_partners_filter']
    delete queryParams['attachments_extensions_filter']
    delete queryParams['attachments_sort_field']
    delete queryParams['attachments_sort_dir']

    return `${location.pathname}?${stringifyQuery(queryParams)}`
  }

  const onSortFieldChange = ({ sortField, sortDir }) => {
    updateURL({
      attachments_sort_field: sortField,
      attachments_sort_dir: sortDir,
    })
  }

  const isAnyActiveFilters = partnersFilter.length > 0 || extensionsFilter.length > 0
  const isAnyActiveFiltersOrSearch = searchValue || isAnyActiveFilters
  const showFilterPanel = attachmentsCount > 0 || isAnyActiveFiltersOrSearch

  return (
    <ExpandablePageSection
      id='attachments'
      title={t('ticketProviderTicketPage.attachments')}
      badgeText={attachmentsCount}
      isExpanded={isExpanded}
      onToggleExpanded={toggleExpanded}
      dataTest='ticket-attachments-section'
    >
      <FilterAndTable>
        <LoadingIndicator loading={isFetching} small />

        {showFilterPanel && (
          <StyledFilterPanel dataTest='ticket-attachments-filter-panel'>
            <FilterPanelTitle
              withoutIcon
              title={t('ticketProviderTicketPage.filteredBy')}
            />
            <FilterWithDropdown
              onChange={(v) => onFilterChange(v, 'attachments_partners_filter')}
              value={partnersFilter}
              options={partnersOptions}
              iconName='icon-partner'
              placeholder={t('ticketProviderTicketPage.attachmentsFilter.partnersFilterPlaceholder')}
              isMulti
              noAlphabetOrder
              withBadges
              dataTest='partners-filter'
            />
            <FilterWithDropdown
              onChange={(v) => onFilterChange(v, 'attachments_extensions_filter')}
              value={extensionsFilter}
              options={extensionsOptions}
              iconName='icon-apps'
              placeholder={t('ticketProviderTicketPage.attachmentsFilter.extensionsFilterPlaceholder')}
              isMulti
              noAlphabetOrder
              dataTest='extensions-filter'
            />

            <UnwrappableSection>
              <FilterPanelSearch
                key={searchValue}
                initialValue={searchValue}
                onChange={onSearch}
                placeholder={t('ticketProviderTicketPage.attachmentsFilter.searchPlaceholder')}
                buttonTooltip={t('ticketProviderTicketPage.attachmentsFilter.filterPanelSearchTooltip')}
                tooltipId='tooltip-attachments-filter-panel'
              />
              {isAnyActiveFiltersOrSearch && (
                <ClearFiltersIcon
                  clearUrl={getClearFiltersUrl()}
                  tooltipText={t('ticketsPage.clearFiltersLink')}
                />
              )}
              <div />
            </UnwrappableSection>

            <SortDropdown
              title={t('common.sortedBy')}
              onChange={onSortFieldChange}
              value={{ sortField: sortField, sortDir: sortDir }}
              options={sortFieldsOptions}
              defaultSortField='external_created_at'
              defaultSortDir='desc'
              clearActionTooltip={t('ticketProviderTicketPage.attachmentsFilter.filterPanelClearSortingTooltip')}
            />
            <Tooltip id='tooltip-attachments-filter-panel' />
          </StyledFilterPanel>
        )}
        <AttachmentsTable
          ticket={ticket}
          attachments={attachments}
          anchorElementId={anchorElementId}
          searchValue={searchValue}
        />
        <Pagination
          activePage={activePage}
          totalPages={totalPages}
          disabled={isFetching}
          pageParamName='attachments_page'
        />
      </FilterAndTable>
    </ExpandablePageSection>
  )
}

TicketAttachments.propTypes = {
  ticket: PropTypes.object.isRequired,
}

export default TicketAttachments
